import React, { useState } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedToolTip } from "@/components/ThemedToolTip";

/**
 * Definition of an individual view mode option.
 */
export interface IViewModeOption<T extends string = string> {
  key: T;
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
}

/**
 * Default standard view modes supported across all item lists corresponding to the 5 Card Tiers.
 */
export const DEFAULT_VIEW_MODES: IViewModeOption[] = [
  { key: "chip", icon: "pricetag-outline", title: "Chip (Tier 1)" },
  { key: "compact", icon: "reorder-four-outline", title: "Compact (Tier 2)" },
  { key: "medium", icon: "apps-outline", title: "Medium (Tier 3)" },
  { key: "full", icon: "newspaper-outline", title: "Full (Tier 4)" },
  { key: "custom", icon: "options-outline", title: "Custom (Tier 5)" },
];

/**
 * Props for the ThemedViewSelector component.
 */
export interface IThemedViewSelectorProps<T extends string = string> {
  /** The currently selected view mode */
  activeMode: T;
  /** Callback fired when the user selects a new view mode */
  onModeChange: (mode: T) => void;
  /** Optional custom list of view modes (defaults to DEFAULT_VIEW_MODES) */
  options?: IViewModeOption<T>[];
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Optional button style override */
  buttonStyle?: StyleProp<ViewStyle>;
}

/**
 * Single view option button with interactive hover border and tooltip integration.
 */
function ThemedViewButton<T extends string>({
  option,
  isActive,
  onPress,
  style,
}: {
  option: IViewModeOption<T>;
  isActive: boolean;
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
}): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const tintColor = useThemeColor({}, "tint");
  const borderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const buttonSecondaryBg = useThemeColor({}, "buttonSecondary");
  const textMuted = useThemeColor({}, "textMuted");

  const resolvedBorder = isActive
    ? tintColor
    : isHovered
    ? hoverBorderColor
    : borderColor;

  const resolvedBg = isActive ? tintColor : buttonSecondaryBg;
  const iconColor = isActive ? "#FFFFFF" : isHovered ? tintColor : textMuted;

  return (
    <ThemedToolTip text={option.title} position="top">
      <TouchableOpacity
        onPress={onPress}
        style={[
          styles.viewButton,
          {
            backgroundColor: resolvedBg,
            borderColor: resolvedBorder,
          },
          isActive && styles.viewButtonActive,
          isHovered && !isActive && styles.viewButtonHovered,
          Platform.OS === "web" && ({ cursor: "pointer" } as any),
          style,
        ]}
        accessibilityLabel={option.title}
        accessibilityRole="button"
        accessibilityState={{ selected: isActive }}
        activeOpacity={0.75}
        {...(Platform.OS === "web"
          ? {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            }
          : {})}
      >
        <Ionicons name={option.icon} size={15} color={iconColor} />
      </TouchableOpacity>
    </ThemedToolTip>
  );
}

/**
 * Standardised View Options button group conforming to Memories UX & Button Standards:
 * - Rendered as individual styled buttons with square zero-radius borders
 * - Built-in ThemedToolTip providing titles on hover over
 * - Active contrast highlights with interactive hover border feedback
 *
 * @param {IThemedViewSelectorProps} props - Component properties.
 * @returns {React.JSX.Element} The rendered button group.
 */
export function ThemedViewSelector<T extends string = string>({
  activeMode,
  onModeChange,
  options = DEFAULT_VIEW_MODES as IViewModeOption<T>[],
  style,
  buttonStyle,
}: IThemedViewSelectorProps<T>): React.JSX.Element {
  return (
    <View style={[styles.container, style]}>
      {options.map((option) => (
        <ThemedViewButton<T>
          key={option.key}
          option={option}
          isActive={activeMode === option.key}
          onPress={() => onModeChange(option.key)}
          style={buttonStyle}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  viewButton: {
    width: 28,
    height: 28,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  viewButtonActive: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  viewButtonHovered: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
});
